import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import algoliasearch from "algoliasearch";
import type { GlobalHit } from "@/components/AlgoliaSearch";
import type { MentionExpandContext, MentionSearchResult, MentionUser } from "@/components/AtMention/types";
import useCapabilities from "@/hooks/useCapabilities";
import useCurrentUser from "@/hooks/useCurrentUser";
import useMentionSearch from "@/hooks/useMentionSearch";
import { parseStateFromLocation } from "@/lib/geo/pgaSectionStates";
import { rankQuickAddMentions, type GeoProximityContext } from "@/lib/geo/rankQuickAddMentions";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import {
  EVENTS_FOR_REFERENCE_SEARCH_QUERY,
  type EventsForReferenceSearchResponse,
  type EventsForReferenceSearchVariables
} from "@/pages/tasks/[taskId]/_hooks/eventsForReferenceSearchQuery";
import { useReferencePickerItems } from "@/pages/tasks/[taskId]/_hooks/useReferencePickerItems";
import type { Section } from "@/types";
import { ME_CAREER_CONSULTANT_SECTIONS_QUERY, type MeCareerConsultantSectionsResponse } from "./queries";
import {
  BACK_MENTION_USER,
  mapAlgoliaHitToMention,
  mapEventToMention,
  mapPickerItemToMention,
  QUICK_ADD_EXPAND_OVERLAY,
  QUICK_ADD_MENTION_OVERLAY,
  teammateMention,
  toLookupContext
} from "./quickAddMentionMappers";

const EVENT_SEARCH_LIMIT = 10;
const ALGOLIA_HITS_PER_PAGE = 15;
const RESTRICT_SEARCHABLE_ATTRIBUTES = [
  "name",
  "preferred_name",
  "facility_name",
  "section_name",
  "pga_id",
  "phone",
  "phones",
  "legacy_id",
  "email"
];

function createSearchIndex() {
  const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
  const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
  const indexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;
  if (!appId || !apiKey || !indexName) return null;
  return algoliasearch(appId, apiKey).initIndex(indexName);
}

export default function useQuickAddMentionSearch(query: string): MentionSearchResult {
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [expandContext, setExpandContext] = useState<MentionExpandContext | null>(null);
  const [algoliaHits, setAlgoliaHits] = useState<GlobalHit[]>([]);
  const [algoliaLoading, setAlgoliaLoading] = useState(false);
  const searchIndex = useMemo(() => createSearchIndex(), []);

  useEffect(() => {
    if (!query) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setExpandContext(null);
  }, [query]);

  const teammateQuery = expandContext ? "" : query;
  const { users: teammateUsers, isLoading: teammatesLoading } = useMentionSearch(teammateQuery);

  const { sectionLegacyIds } = useCapabilities();
  const {
    user: { location }
  } = useCurrentUser();

  const skipRankingQueries = !debouncedQuery;

  const { data: consultantData } = useQuery<MeCareerConsultantSectionsResponse>(ME_CAREER_CONSULTANT_SECTIONS_QUERY, {
    skip: skipRankingQueries,
    errorPolicy: "all",
    fetchPolicy: "cache-first"
  });

  const { data: sectionsData } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: skipRankingQueries || sectionLegacyIds.length === 0,
    fetchPolicy: "cache-first"
  });

  const { data: eventsData, loading: eventsLoading } = useQuery<
    EventsForReferenceSearchResponse,
    EventsForReferenceSearchVariables
  >(EVENTS_FOR_REFERENCE_SEARCH_QUERY, {
    variables: { query: { title: debouncedQuery }, first: EVENT_SEARCH_LIMIT },
    skip: !debouncedQuery || !!expandContext
  });

  const lookupContext = expandContext ? toLookupContext(expandContext) : null;
  const { items: pickerItems, loading: pickerLoading } = useReferencePickerItems(lookupContext);

  useEffect(() => {
    if (!debouncedQuery || expandContext || !searchIndex) {
      setAlgoliaHits([]);
      setAlgoliaLoading(false);
      return;
    }

    let cancelled = false;
    setAlgoliaLoading(true);
    searchIndex
      .search<GlobalHit>(debouncedQuery, {
        hitsPerPage: ALGOLIA_HITS_PER_PAGE,
        filters: "object_type:Person OR object_type:Facility",
        restrictSearchableAttributes: RESTRICT_SEARCHABLE_ATTRIBUTES
      })
      .then((result) => {
        if (!cancelled) setAlgoliaHits(result.hits);
      })
      .catch(() => {
        if (!cancelled) setAlgoliaHits([]);
      })
      .finally(() => {
        if (!cancelled) setAlgoliaLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, expandContext, searchIndex]);

  const geoContext: GeoProximityContext = useMemo(() => {
    const assignedIds = new Set(sectionLegacyIds);
    const assignedSectionNames = (sectionsData?.sections ?? [])
      .filter(
        (section) => Boolean(section.legacyId && assignedIds.has(section.legacyId)) || assignedIds.has(section.id)
      )
      .map((section) => section.name);
    return {
      consultantSectionNames: consultantData?.careerConsultantSections?.map((section) => section.name) ?? [],
      assignedSectionNames,
      userLocationState: parseStateFromLocation(location)
    };
  }, [consultantData, location, sectionLegacyIds, sectionsData]);

  const personInsertReady = useMemo(() => pickerItems.some((item) => item.__typename === "CRMPerson"), [pickerItems]);
  const personExpandPendingParent = expandContext?.type === "Person" && pickerLoading && !personInsertReady;

  const users = useMemo(() => {
    if (expandContext) {
      const visibleItems = personExpandPendingParent ? [] : pickerItems;
      const children = visibleItems.map(mapPickerItemToMention).filter((item): item is MentionUser => item !== null);
      return [BACK_MENTION_USER, ...children];
    }

    const teammates = teammateUsers.map(teammateMention);
    const records = algoliaHits.map(mapAlgoliaHitToMention).filter((item): item is MentionUser => item !== null);
    const events = (eventsData?.events?.edges ?? []).map(({ node }) => mapEventToMention(node));
    return rankQuickAddMentions([...teammates, ...records, ...events], query, geoContext);
  }, [
    algoliaHits,
    eventsData,
    expandContext,
    geoContext,
    personExpandPendingParent,
    pickerItems,
    query,
    teammateUsers
  ]);

  const resolveSelect = useCallback((user: MentionUser) => {
    if (user.selectAction === "back") {
      setExpandContext(null);
      return false;
    }
    if (user.selectAction === "expand" && user.expandContext) {
      setExpandContext(user.expandContext);
      return false;
    }
    return true;
  }, []);

  const dismiss = useCallback(() => {
    if (expandContext) {
      setExpandContext(null);
      return false;
    }
    return true;
  }, [expandContext]);

  const isLoading = expandContext
    ? personExpandPendingParent || (pickerLoading && pickerItems.length === 0)
    : (teammatesLoading || algoliaLoading || eventsLoading) && users.length === 0;

  return {
    users,
    isLoading,
    resolveSelect,
    dismiss,
    overlay: expandContext ? QUICK_ADD_EXPAND_OVERLAY : QUICK_ADD_MENTION_OVERLAY
  };
}
